import { supabase } from '@/lib/supabase';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';

export interface PricingPlan {
  id: string;
  stripe_product_id: string;
  stripe_price_id: string;
  name: string;
  description: string | null;
  amount_cents: number;
  currency: string;
  billing_period: string;
  interval_count: number;
  trial_period_days: number;
  is_active: boolean;
  slug: string;
  features: string[];
  auto_subscribe: boolean;
  metadata: {
    tier?: string;
    sort_order?: number;
    stripe_product?: any;
    stripe_price?: any;
    base_plan_slug?: string | null;
  };
  created_at: string;
  updated_at: string;
}

export const PricingService = {
  /**
   * Get all global pricing plans from Supabase (synced from Stripe)
   */
  async getGlobalPricingPlans(): Promise<PricingPlan[]> {
    const context: ErrorContext = {
      operation: 'getGlobalPricingPlans',
      additionalInfo: {}
    };

    try {
      const { data: plans, error } = await supabase
        .from('subscription_prices')
        .select('*')
        .eq('is_global', true)
        .eq('is_active', true)
        .order('amount_cents', { ascending: true });

      if (error) {
        throw error;
      }

      // Transform and sort the plans
      const transformedPlans = (plans || []).map(plan => ({
        ...plan,
        features: Array.isArray(plan.features) ? plan.features : [],
        metadata: plan.metadata || {},
        auto_subscribe: plan.auto_subscribe ?? true // Default to true for backward compatibility
      }));

      return transformedPlans;
    } catch (error) {
      handleError(error, "Failed to fetch global pricing plans", ErrorSeverity.ERROR, context);
      return [];
    }
  },

  /**
   * Get a specific pricing plan by slug
   */
  async getPricingPlanBySlug(slug: string): Promise<PricingPlan | null> {
    const context: ErrorContext = {
      operation: 'getPricingPlanBySlug',
      additionalInfo: { slug }
    };

    try {
      const { data: plan, error } = await supabase
        .from('subscription_prices')
        .select('*')
        .eq('is_global', true)
        .eq('is_active', true)
        .eq('slug', slug)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          return null;
        }
        throw error;
      }

      return {
        ...plan,
        features: Array.isArray(plan.features) ? plan.features : [],
        metadata: plan.metadata || {},
        auto_subscribe: plan.auto_subscribe ?? true // Default to true for backward compatibility
      };
    } catch (error) {
      handleError(error, "Failed to fetch pricing plan by slug", ErrorSeverity.ERROR, context);
      return null;
    }
  },

  /**
   * Sync pricing plans from Stripe (calls the Edge Function)
   */
  async syncFromStripe(): Promise<{ success: boolean; message: string; synced_count?: number }> {
    const context: ErrorContext = {
      operation: 'syncFromStripe',
      additionalInfo: {}
    };

    try {
      const { data, error } = await supabase.functions.invoke('sync-stripe-pricing', {
        body: {}
      });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      handleError(error, "Failed to sync pricing from Stripe", ErrorSeverity.ERROR, context);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Format price for display
   */
  formatPrice(amountCents: number, currency: string = 'usd', billingPeriod: string = 'month'): string {
    const amount = amountCents / 100;
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);

    const periodMap: { [key: string]: string } = {
      'month': 'month',
      'year': 'year',
      'week': 'week',
      'day': 'day'
    };

    const period = periodMap[billingPeriod] || billingPeriod;
    return `${formattedAmount}/${period}`;
  },

  /**
   * Get trial period display text
   */
  getTrialText(trialDays: number): string {
    if (trialDays <= 0) return '';
    return `${trialDays}-day free trial`;
  },

  /**
   * Get auto-subscribable plans only (for self-serve checkout)
   */
  async getAutoSubscribablePlans(): Promise<PricingPlan[]> {
    const allPlans = await this.getGlobalPricingPlans();
    return allPlans.filter(plan => plan.auto_subscribe);
  },

  /**
   * Get contact-only plans (enterprise/custom plans)
   */
  async getContactOnlyPlans(): Promise<PricingPlan[]> {
    const allPlans = await this.getGlobalPricingPlans();
    return allPlans.filter(plan => !plan.auto_subscribe);
  },

  /**
   * Get the base plan name for feature inheritance display
   */
  getBasePlanDisplayName(plan: PricingPlan): string | null {
    const basePlanSlug = plan.metadata?.base_plan_slug;
    if (!basePlanSlug) return null;
    
    // Convert slug to display name (e.g., "essential" -> "Essential")
    return basePlanSlug.charAt(0).toUpperCase() + basePlanSlug.slice(1);
  },

  /**
   * Get feature prefix text for inheritance display
   */
  getFeaturePrefix(plan: PricingPlan): string {
    const basePlanName = this.getBasePlanDisplayName(plan);
    if (!basePlanName) return '';
    
    return `Everything in ${basePlanName}, plus:`;
  },

  /**
   * Check if a plan should show "Get Started" or "Contact Sales"
   */
  getCallToActionText(plan: PricingPlan): { text: string; variant: 'primary' | 'secondary' } {
    if (plan.auto_subscribe) {
      return { text: 'Get Started', variant: 'primary' };
    } else {
      return { text: 'Contact Sales', variant: 'secondary' };
    }
  }
};